import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { useTheme } from '../context/ThemeContext';
import { CheckIcon, StarIcon } from '@heroicons/react/24/solid';
import {
  ShieldCheckIcon, ClipboardDocumentListIcon, BeakerIcon,
  SparklesIcon, DocumentMagnifyingGlassIcon, LinkIcon,
} from '@heroicons/react/24/outline';

const PRICE = 25;
const STARTER_PRICE = 5;
const TRIAL_DAYS = 30;

export default function PricingPage() {
  const { user } = useAuth();
  const { dark } = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [starterLoading, setStarterLoading] = useState(false);
  const [starterError, setStarterError] = useState('');

  async function handleSubscribe() {
    if (!user) {
      navigate('/register');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/billing/create-checkout-session');
      window.location.href = res.data.url;
    } catch (err) {
      setError(err.response?.data?.error || 'Unable to start checkout. Please try again.');
      setLoading(false);
    }
  }

  async function handleStarterSubscribe() {
    if (!user) {
      navigate('/register');
      return;
    }
    setStarterLoading(true);
    setStarterError('');
    try {
      const res = await api.post('/billing/create-starter-checkout-session');
      window.location.href = res.data.url;
    } catch (err) {
      setStarterError(err.response?.data?.error || 'Unable to start checkout. Please try again.');
      setStarterLoading(false);
    }
  }

  const features = [
    'Upload unlimited lab reports (PDF or text)',
    'Plain-language explanations at your reading level',
    'Results in your preferred language',
    'Trend tracking across multiple visits',
    'AI-generated questions to ask your doctor',
    'Secure, HIPAA-aligned data handling',
    'Shareable reports for family or caregivers',
    'Priority support',
  ];

  const premiumFeatures = [
    {
      icon: ClipboardDocumentListIcon,
      title: 'Appointment Prep Brief',
      desc: 'Before every doctor visit, generate a personalized summary of your lab history — key findings, questions to ask, and values to watch. Print it and bring it with you.',
      color: 'sky',
    },
    {
      icon: BeakerIcon,
      title: 'Medication Interaction Check',
      desc: 'Enter your current medications and get an AI-powered interaction check cross-referenced with your actual lab values. Spot concerns before your appointment.',
      color: 'teal',
    },
    {
      icon: ShieldCheckIcon,
      title: 'Preventive Care Gaps',
      desc: 'Based on your test history and USPSTF guidelines, discover screenings you may be overdue for. Stay ahead of preventive care — not behind it.',
      color: 'green',
    },
    {
      icon: DocumentMagnifyingGlassIcon,
      title: 'Ask Questions About Any Report',
      desc: 'Select any of your lab reports and ask specific questions in plain language. Get answers grounded in your actual data — not generic advice.',
      color: 'indigo',
    },
    {
      icon: LinkIcon,
      title: 'Connected Health Providers',
      desc: 'Link your insurance portal or EHR (Epic, Cerner, and more) to automatically import lab results — no manual uploads needed.',
      color: 'violet',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 to-white dark:from-slate-950 dark:to-slate-900">
      {/* Nav */}
      <nav className="flex items-center justify-between px-6 py-4 max-w-5xl mx-auto">
        <Link to="/" className="flex items-center gap-2">
          <img src={dark ? '/logo-dark.svg?v=3' : '/logo.svg?v=3'} alt="HealthClear AI" className="h-9 w-auto" />
        </Link>
        <div className="flex items-center gap-3">
          {user ? (
            <Link to="/app/dashboard" className="text-sm text-sky-600 font-medium hover:text-sky-700">
              Go to dashboard →
            </Link>
          ) : (
            <>
              <Link to="/login" className="text-sm text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-white">Sign in</Link>
              <Link to="/register" className="text-sm bg-sky-600 text-white px-4 py-2 rounded-lg hover:bg-sky-700 font-medium">
                Start free
              </Link>
            </>
          )}
        </div>
      </nav>

      {/* Hero */}
      <div className="text-center px-6 pt-14 pb-8 max-w-2xl mx-auto">
        <h1 className="text-4xl font-bold text-slate-800 dark:text-white leading-tight">
          Simple, honest pricing
        </h1>
        <p className="mt-3 text-lg text-slate-500 dark:text-slate-400">
          Start free for {TRIAL_DAYS} days. No credit card required.
        </p>
      </div>

      {/* Pricing cards */}
      <div className="max-w-3xl mx-auto px-6 mb-12">
        <div className="grid sm:grid-cols-2 gap-6 items-stretch">

          {/* Starter card */}
          <div className="bg-white dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-600 rounded-3xl shadow-md overflow-hidden flex flex-col">
            <div className="bg-slate-800 px-6 pt-7 pb-5 text-white text-center">
              <span className="text-xs font-semibold uppercase tracking-widest text-slate-300">Starter</span>
              <div className="flex items-end justify-center gap-1 mt-2">
                <span className="text-4xl font-bold">${STARTER_PRICE}</span>
                <span className="text-slate-300 mb-1.5">/month</span>
              </div>
              <p className="mt-2 text-slate-300 text-sm">Keep your progress. Stay connected.</p>
            </div>
            <div className="px-6 py-5 flex flex-col flex-1">
              <ul className="space-y-2.5 mb-5">
                {[
                  'View all your past results & history',
                  'Upload 2 new reports per month',
                  'Plain-language explanations',
                  'See what\'s flagged or in range',
                  'Secure, HIPAA-aligned storage',
                ].map((f, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckIcon className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-600 dark:text-slate-300">{f}</span>
                  </li>
                ))}
              </ul>
              {starterError && (
                <p className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg px-3 py-2 mb-3">{starterError}</p>
              )}
              <div className="mt-auto">
                {user?.subscriptionStatus === 'starter' ? (
                  <>
                    <div className="w-full flex items-center justify-center gap-2 bg-slate-100 dark:bg-slate-700 text-slate-400 font-semibold py-3 rounded-xl cursor-default select-none">
                      <CheckIcon className="w-4 h-4" />
                      Current plan
                    </div>
                    <p className="text-center text-xs text-slate-400 mt-2">You're on this plan</p>
                  </>
                ) : (
                  <>
                    <button
                      onClick={handleStarterSubscribe}
                      disabled={starterLoading || user?.subscriptionStatus === 'active'}
                      className="w-full bg-slate-800 hover:bg-slate-700 text-white font-semibold py-3 rounded-xl transition-colors disabled:opacity-60"
                    >
                      {starterLoading ? 'Redirecting…' : user ? 'Get Starter plan' : 'Start free trial'}
                    </button>
                    <p className="text-center text-xs text-slate-400 mt-2">
                      Upgrade to Full Access any time
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Full Access card */}
          <div className="bg-white dark:bg-slate-800 border-2 border-sky-500 rounded-3xl shadow-xl overflow-hidden flex flex-col">
            <div className="bg-sky-600 px-6 pt-7 pb-5 text-white text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <StarIcon className="w-4 h-4 text-amber-300" />
                <span className="text-xs font-semibold uppercase tracking-widest text-sky-200">Full Access</span>
              </div>
              <div className="flex items-end justify-center gap-1 mt-2">
                <span className="text-4xl font-bold">${PRICE}</span>
                <span className="text-sky-200 mb-1.5">/month</span>
              </div>
              <p className="mt-2 text-sky-100 text-sm">{TRIAL_DAYS}-day free trial included</p>
            </div>
            <div className="px-6 py-5 flex flex-col flex-1">
              <ul className="space-y-2.5 mb-5">
                {features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckIcon className="w-4 h-4 text-sky-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700 dark:text-slate-200">{f}</span>
                  </li>
                ))}
              </ul>
              {error && (
                <p className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg px-3 py-2 mb-3">{error}</p>
              )}
              <div className="mt-auto">
                {user?.subscriptionStatus === 'active' ? (
                  <>
                    <div className="w-full flex items-center justify-center gap-2 bg-sky-100 dark:bg-sky-900/30 text-sky-400 dark:text-sky-300 font-semibold py-3 rounded-xl cursor-default select-none">
                      <CheckIcon className="w-4 h-4" />
                      Current plan
                    </div>
                    <p className="text-center text-xs text-slate-400 mt-2">You're on this plan</p>
                  </>
                ) : (
                  <>
                    <button
                      onClick={handleSubscribe}
                      disabled={loading}
                      className="w-full bg-sky-600 hover:bg-sky-700 text-white font-semibold py-3 rounded-xl transition-colors disabled:opacity-60"
                    >
                      {loading ? 'Redirecting…' : user?.subscriptionStatus === 'starter' ? 'Upgrade to Full Access' : user ? 'Subscribe now' : 'Start free trial'}
                    </button>
                    {!user && (
                      <p className="text-center text-xs text-slate-400 mt-2">
                        No credit card required for trial
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Features breakdown */}
      <div className="max-w-5xl mx-auto px-6 mb-14">
        {/* Section header */}
        <div className="text-center mb-10">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white">What's included in each plan</h2>
          <p className="text-slate-500 dark:text-slate-400 mt-2 text-sm max-w-lg mx-auto">
            Every plan gives you access to your health data. Full Access unlocks the AI tools built to help you act on it.
          </p>
        </div>

        {/* Two-column feature lists — same style */}
        <div className="grid sm:grid-cols-2 gap-6 items-start mb-14">

          {/* Starter column */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <span className="text-xs font-semibold uppercase tracking-widest text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 px-3 py-1 rounded-full">
                Starter — $5/mo
              </span>
            </div>
            <div className="space-y-2">
              {[
                'View all your past results & history',
                'Upload 2 new reports per month',
                'Plain-language explanations',
                "See what's flagged, borderline, or in range",
                'Secure, HIPAA-aligned storage',
              ].map((f, i) => (
                <div key={i} className="flex items-start gap-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-xl px-4 py-3">
                  <CheckIcon className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-slate-700 dark:text-slate-200">{f}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Full Access column */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <span className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-widest text-sky-700 bg-sky-50 border border-sky-200 px-3 py-1 rounded-full">
                <SparklesIcon className="w-3 h-3" /> Full Access — $25/mo
              </span>
            </div>
            <p className="text-xs text-slate-400 dark:text-slate-500 mb-3 ml-1">Everything in Starter, plus:</p>
            <div className="space-y-2">
              {[
                'Unlimited uploads',
                'Trend tracking across multiple visits',
                'Appointment prep brief for doctor visits',
                'Medication interaction check vs. your labs',
                'Preventive care gaps (USPSTF guidelines)',
                'Ask questions about any report',
                'Connected health provider integrations',
              ].map((f, i) => (
                <div key={i} className="flex items-start gap-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-xl px-4 py-3">
                  <CheckIcon className="w-4 h-4 text-sky-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-slate-700 dark:text-slate-200">{f}</span>
                </div>
              ))}
            </div>
            <p className="text-center text-xs text-slate-400 mt-3">
              Unlimited uploads · No usage limits · No extra charges
            </p>
          </div>
        </div>

        {/* FAQ — below both columns */}
        <div>
          <h2 className="text-xl font-semibold text-slate-800 dark:text-white mb-6 text-center">Frequently asked questions</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            {[
              {
                q: 'What happens after my free trial?',
                a: `After your ${TRIAL_DAYS}-day trial, you can choose the Starter plan ($${STARTER_PRICE}/mo) to keep your history and upload 2 reports/month, or subscribe to Full Access ($${PRICE}/mo) for unlimited uploads and all AI features.`,
              },
              {
                q: 'Can I cancel anytime?',
                a: 'Absolutely. Cancel from your profile at any time — no fees, no questions asked. Your subscription remains active until the end of the billing period.',
              },
              {
                q: 'Is my health data safe?',
                a: 'Yes. All data is encrypted at rest and in transit. We follow HIPAA-aligned practices and never sell your data.',
              },
            ].map((item, i) => (
              <div key={i} className="bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 px-5 py-4">
                <p className="font-medium text-slate-800 dark:text-white text-sm">{item.q}</p>
                <p className="text-slate-500 dark:text-slate-400 text-sm mt-1.5 leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>

          {/* Trust badges */}
          <div className="flex flex-wrap justify-center gap-6 text-sm text-slate-500 mt-6">
            <div className="flex items-center gap-1.5">
              <ShieldCheckIcon className="w-4 h-4 text-slate-400" />
              HIPAA-aligned
            </div>
            <div className="flex items-center gap-1.5">
              <ShieldCheckIcon className="w-4 h-4 text-slate-400" />
              Cancel anytime
            </div>
            <div className="flex items-center gap-1.5">
              <ShieldCheckIcon className="w-4 h-4 text-slate-400" />
              Secure checkout by Stripe
            </div>
          </div>
        </div>
      </div>

      {/* Contact */}
      <div className="border-t border-slate-100 dark:border-slate-800 py-8 text-center">
        <p className="text-xs text-slate-400">
          6900 Tavistock Lakes Blvd. Suite 400, Orlando FL, 32827 · <a href="tel:6893292013" className="hover:text-slate-600 text-slate-400">689-329-2013</a>
        </p>
        <p className="text-xs text-slate-400 mt-2">© 2026 HealthClear AI. All rights reserved.</p>
      </div>
    </div>
  );
}
